import {
  INFO_FIELDS,
  fieldLabel,
  formatFieldValue,
} from "./fieldMetadata";

// Renders the confirm panel — checklist of parsed fields, manual review
// notes, Apply button. Pure presentation; all state lives in the hook.

export default function ConfirmUI({ state, handlers }) {
  const { importData, applyState } = state;
  const { reset, handleApply, toggleChecked, toggleCollapsed } = handlers;

  const parseResult = importData.result;
  const applied = !!importData.applied;
  const checked = importData.checked || {};
  const collapsed = !!importData.collapsed;
  const parsed = parseResult.parsedFields || {};
  const populatedKeys = Object.keys(parsed).filter(
    (k) => parsed[k] !== null && parsed[k] !== undefined && parsed[k] !== ""
  );
  const flaggedSet = new Set(parseResult.flaggedFields || []);
  const unmappedKeys =
    parseResult.unmappedFields ||
    Object.keys(parsed).filter(
      (k) => parsed[k] === null || parsed[k] === undefined || parsed[k] === ""
    );
  const notes = parseResult.manualReviewNotes || [];
  const sources = parseResult.fieldSources || {};

  return (
    <div className="rounded-xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={
            collapsed ? "Expand parse results" : "Collapse parse results"
          }
          className="flex flex-1 items-center gap-2 text-left"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className={`h-4 w-4 text-gray-500 transition-transform ${
              collapsed ? "-rotate-90" : ""
            }`}
            fill="currentColor"
          >
            <path d="M5.5 7.5l4.5 5 4.5-5z" />
          </svg>
          <h3 className="text-sm font-semibold text-textcolor">
            {applied ? "Imported fields" : "Review extracted fields"}
          </h3>
          {applied && (
            <span className="ml-1 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
              Applied
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={reset}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Start over
        </button>
      </div>

      {!collapsed && (
        <>
          <p className="mt-1 text-xs text-gray-500">
            {applied
              ? "These values were copied into the form. Edit them below or re-apply after changes."
              : "Uncheck anything you don't want to import."}
          </p>

          <ul className="mt-3 space-y-2">
            {populatedKeys.length === 0 && (
              <li className="text-xs italic text-gray-500">
                No fields were extracted automatically.
              </li>
            )}
            {populatedKeys.map((key) => {
              const isInfo = INFO_FIELDS.has(key);
              return (
                <li
                  key={key}
                  className="flex items-center justify-between rounded-md border border-black/5 bg-gray-50 px-3 py-2"
                >
                  {isInfo ? (
                    <span className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-textcolor">
                        {fieldLabel(key)}
                      </span>
                      <span className="text-xs italic text-gray-400">
                        (reference)
                      </span>
                    </span>
                  ) : (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-testid={`field-checkbox-${key}`}
                        checked={!!checked[key]}
                        onChange={() => toggleChecked(key)}
                      />
                      <span className="font-medium text-textcolor">
                        {fieldLabel(key)}
                      </span>
                    </label>
                  )}
                  <div className="flex items-baseline gap-2">
                    {sources[key] && (
                      <span
                        data-testid={`field-source-${key}`}
                        className="text-xs text-gray-400"
                      >
                        {sources[key]}
                      </span>
                    )}
                    <span className="text-sm text-gray-700">
                      {formatFieldValue(key, parsed[key])}
                    </span>
                    {flaggedSet.has(key) && (
                      <span
                        data-testid={`field-verify-${key}`}
                        className="ml-1 inline-flex shrink-0 items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                      >
                        verify
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {unmappedKeys.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Needs manual entry
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-gray-700">
                {unmappedKeys.map((k) => (
                  <li key={k}>{fieldLabel(k)}</li>
                ))}
              </ul>
            </div>
          )}

          {notes.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Manual review
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-amber-900">
                {notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleApply}
              disabled={applyState === "applying"}
              className="rounded-lg bg-[#1E293B] px-3 py-1.5 text-sm text-white hover:bg-[#0f172a] disabled:opacity-50"
            >
              {applyState === "applying"
                ? "Applying…"
                : applied
                ? "Re-apply"
                : "Apply"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
